import { useForm, usePage, router } from "@inertiajs/react"
import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Pagination } from "@/components/ui/pagination"
import { FlashMessages } from "@/components/FlashMessages"
import { InventoryFilters } from "@/components/InventoryFilters"
import { InventoryTable } from "@/components/InventoryTable"
import { AssetForm } from "@/components/AssetForm"
import { MoveStageDialog } from "@/components/MoveStageDialog"
import { MoveLocationDialog } from "@/components/MoveLocationDialog"
import { Plus, Package, ArrowRightLeft, X, Download, MapPin, ScanLine } from "lucide-react"
import type { Asset, AssetFormData, InventoryFilters as InventoryFiltersType, InventoryPageProps } from "@/types/inventory"
import { emptyAssetFormData } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"

export default function Inventory() {
  const { t } = useTranslation()
  const {
    auth,
    flash,
    assets,
    pagination,
    filters,
    assetType,
    assetTypes,
    stages,
    customers,
    suppliers,
    locations,
    tags,
    products,
    containers,
    volumes,
  } = usePage<InventoryPageProps>().props

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isRequestTypeDialogOpen, setIsRequestTypeDialogOpen] = useState(false)
  const [createAssetType, setCreateAssetType] = useState<AssetType>(assetType)
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
  const [selectedAssets, setSelectedAssets] = useState<Map<string, Asset>>(new Map())
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false)
  const [isBulkMove, setIsBulkMove] = useState(false)
  const [moveDialogAssets, setMoveDialogAssets] = useState<Asset[]>([])
  const [isMoveLocationDialogOpen, setIsMoveLocationDialogOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const defaultFormData: AssetFormData = {
    ...emptyAssetFormData,
    stage_id: stages[0]?.id || "",
  }

  const createForm = useForm<AssetFormData>(defaultFormData)
  const editForm = useForm<AssetFormData>({ ...emptyAssetFormData })

  const allSelectedCodes = useMemo(() => {
    return Array.from(selectedAssets.values()).map((asset) => asset.code)
  }, [selectedAssets])

  const selectedAssetIds = useMemo(() => {
    return new Set(selectedAssets.keys())
  }, [selectedAssets])

  function buildUrl(extra: Record<string, unknown> = {}) {
    return { asset_type: assetType, ...extra }
  }

  function handleSearch(newFilters: Partial<InventoryFiltersType>) {
    router.get("/inventory", {
      ...buildUrl(newFilters),
      sort: filters.sort,
      direction: filters.direction,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }

  function handleSort(column: string) {
    const newDirection = filters.sort === column && filters.direction === "asc" ? "desc" : "asc"
    router.get("/inventory", {
      asset_type: assetType,
      code: filters.code || undefined,
      product_id: filters.product_id || undefined,
      container_id: filters.container_id || undefined,
      volume: filters.volume || undefined,
      stage_id: filters.stage_id || undefined,
      customer_id: filters.customer_id || undefined,
      location_id: filters.location_id || undefined,
      tag_id: filters.tag_id || undefined,
      sort: column,
      direction: newDirection,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }

  function goToPage(page: number) {
    router.get("/inventory", {
      asset_type: assetType,
      code: filters.code || undefined,
      product_id: filters.product_id || undefined,
      container_id: filters.container_id || undefined,
      volume: filters.volume || undefined,
      stage_id: filters.stage_id || undefined,
      customer_id: filters.customer_id || undefined,
      location_id: filters.location_id || undefined,
      tag_id: filters.tag_id || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, { preserveState: true, preserveScroll: true })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post(`/inventory?asset_type=${createAssetType}`, {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        createForm.setData("stage_id", stages[0]?.id || "")
        createForm.setData("bought_at", new Date().toISOString().split("T")[0])
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingAsset) return

    editForm.patch(`/inventory/${editingAsset.id}?asset_type=${assetType}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingAsset(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(asset: Asset) {
    setEditingAsset(asset)
    editForm.setData({
      ...emptyAssetFormData,
      code: asset.code,
      stage_id: asset.stage.id,
      customer_id: asset.customer?.id || "",
      supplier_id: asset.supplier?.id || "",
      location_id: asset.location?.id || "",
      tag_ids: asset.tags.map((t) => t.id),
      notes: asset.notes || "",
      // Keg
      product_id: asset.product?.id || "",
      container_id: asset.container?.id || "",
      bought_at: asset.bought_at?.split("T")[0] || new Date().toISOString().split("T")[0],
      serial_number: asset.serial_number || "",
      batch_number: asset.batch_number || "",
      expiration_date: asset.expiration_date?.split("T")[0] || "",
      tare: asset.tare?.toString() || "",
      volume: asset.volume?.toString() || "",
      // Fridge / BeerTap
      brand: asset.brand || "",
      model: asset.model || "",
      capacity: asset.capacity?.toString() || "",
      // Cylinder
      pressure_rating: asset.pressure_rating?.toString() || "",
      last_inspection_date: asset.last_inspection_date?.split("T")[0] || "",
      expiry_date: asset.expiry_date?.split("T")[0] || "",
      // BeerTap
      tap_count: asset.tap_count?.toString() || "",
    })
  }

  function handleMoveStage(asset: Asset) {
    setMoveDialogAssets([asset])
    setIsBulkMove(false)
    setIsMoveDialogOpen(true)
  }

  function handleBulkMoveStage() {
    setMoveDialogAssets(Array.from(selectedAssets.values()))
    setIsBulkMove(true)
    setIsMoveDialogOpen(true)
  }

  function handleBulkMoveLocation() {
    setIsMoveLocationDialogOpen(true)
  }

  function handleMoveLocationDialogClose(open: boolean, clearSelectionOnClose = false) {
    setIsMoveLocationDialogOpen(open)
    if (!open && clearSelectionOnClose) {
      clearSelection()
    }
  }

  async function handleBulkDownloadQrCodes() {
    setIsDownloading(true)
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
      const response = await fetch("/inventory/bulk_qr_download", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ asset_ids: Array.from(selectedAssets.keys()) }),
      })

      if (response.status === 204) {
        return
      }

      if (!response.ok) throw new Error("Download failed")

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "qr_codes.zip"
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } finally {
      setIsDownloading(false)
    }
  }

  function handleMoveDialogClose(open: boolean, clearSelectionOnClose = false) {
    setIsMoveDialogOpen(open)
    if (!open) {
      setMoveDialogAssets([])
      if (clearSelectionOnClose) {
        setSelectedAssets(new Map())
      }
    }
  }

  function clearSelection() {
    setSelectedAssets(new Map())
  }

  function handleSelectionChange(ids: Set<string>) {
    const newSelection = new Map(selectedAssets)
    for (const id of selectedAssets.keys()) {
      if (!ids.has(id)) {
        newSelection.delete(id)
      }
    }
    for (const asset of assets) {
      if (ids.has(asset.id) && !newSelection.has(asset.id)) {
        newSelection.set(asset.id, asset)
      }
    }
    setSelectedAssets(newSelection)
  }

  const hasFilters = filters.code || filters.product_id || filters.container_id || filters.volume ||
    filters.stage_id || filters.customer_id || filters.location_id || filters.tag_id

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <PageHeader
          eyebrow={t("inventory.eyebrow", { defaultValue: "Controle de estoque" })}
          title={t("inventory.title")}
          subtitle={t("inventory.subtitle")}
          actions={
            <>
              <Button variant="outline" asChild>
                <a href="/scan"><ScanLine className="w-4 h-4 mr-2" />{t("nav.scan")}</a>
              </Button>
              <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                setIsCreateDialogOpen(open)
                if (!open) {
                  createForm.reset()
                  createForm.setData("stage_id", stages[0]?.id || "")
                  createForm.setData("bought_at", new Date().toISOString().split("T")[0])
                  setCreateAssetType(assetType)
                }
              }}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("inventory.addButton")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <form onSubmit={handleCreateSubmit}>
                    <DialogHeader>
                      <DialogTitle>{t("inventory.addButton")}</DialogTitle>
                      <DialogDescription>{t("inventory.addDescription")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1 mb-4">
                      <label className="text-sm font-medium">{t("inventory.form.assetTypeLabel")}</label>
                      <Select value={createAssetType} onValueChange={(value) => {
                        if (value === "__request_new_type__") {
                          setIsRequestTypeDialogOpen(true)
                          return
                        }
                        const newType = value as AssetType
                        setCreateAssetType(newType)
                        createForm.reset()
                        createForm.setData({
                          ...emptyAssetFormData,
                          stage_id: stages[0]?.id || "",
                          bought_at: new Date().toISOString().split("T")[0],
                        })
                      }}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {assetTypes.map(({ type, key }) => (
                            <SelectItem key={type} value={type}>
                              {t(`assetTypes.${key}`)}
                            </SelectItem>
                          ))}
                          <SelectItem value="__request_new_type__" className="text-muted-foreground">
                            + {t("inventory.form.requestNewType")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <AssetForm
                      data={createForm.data}
                      errors={createForm.errors}
                      onChange={(field, value) => createForm.setData(field, value)}
                      idPrefix="create"
                      assetType={createAssetType}
                      stages={stages}
                      customers={customers}
                      suppliers={suppliers}
                      locations={locations}
                      tags={tags}
                      products={products}
                      containers={containers}
                      hiddenFields={["stage_id", "product_id", "customer_id"]}
                    />
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                        {t("common.cancel")}
                      </Button>
                      <Button type="submit" disabled={createForm.processing}>
                        <Plus className="w-4 h-4 mr-2" />
                        {t("common.add")}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          }
        />
          <Dialog open={isRequestTypeDialogOpen} onOpenChange={setIsRequestTypeDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("inventory.form.requestNewTypeTitle")}</DialogTitle>
                <DialogDescription>{t("inventory.form.requestNewTypeDescription")}</DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <a href="mailto:suporte@biertrack.com" className="text-primary font-medium hover:underline">
                  suporte@biertrack.com
                </a>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRequestTypeDialogOpen(false)}>
                  {t("common.close")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!editingAsset} onOpenChange={(open) => !open && setEditingAsset(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("inventory.editAsset")}</DialogTitle>
                  <DialogDescription>{t("inventory.editDescription")}</DialogDescription>
                </DialogHeader>
                <AssetForm
                  data={editForm.data}
                  errors={editForm.errors}
                  onChange={(field, value) => editForm.setData(field, value)}
                  idPrefix="edit"
                  assetType={assetType}
                  stages={stages}
                  customers={customers}
                  suppliers={suppliers}
                  locations={locations}
                  tags={tags}
                  products={products}
                  containers={containers}
                  hiddenFields={["stage_id", "product_id", "customer_id"]}
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingAsset(null)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                  <Package className="w-5 h-5" />
                  {t("inventory.section")}
                </CardTitle>
                <CardDescription>{t("inventory.sectionDescription")}</CardDescription>
              </div>

              {selectedAssets.size > 0 && (
                <div
                  className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md"
                  style={{ background: "var(--accent-soft)" }}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="font-mono text-[11px] font-semibold tracking-[0.1em] uppercase cursor-help" style={{ color: "var(--foreground)" }}>
                          {selectedAssets.size}{" "}
                          {selectedAssets.size > 1
                            ? t("inventory.selectedAssetsPlural", { defaultValue: "Selecionados" })
                            : t("inventory.selectedAssetsSingular", { defaultValue: "Selecionado" })}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{allSelectedCodes.join(", ")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <Button variant="outline" size="sm" onClick={handleBulkMoveStage}>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    {t("inventory.moveStage")}
                  </Button>

                  <Button variant="outline" size="sm" onClick={handleBulkMoveLocation}>
                    <MapPin className="w-4 h-4 mr-2" />
                    {t("inventory.moveLocation")}
                  </Button>

                  <Button variant="outline" size="sm" onClick={handleBulkDownloadQrCodes} disabled={isDownloading}>
                    <Download className="w-4 h-4 mr-2" />
                    {isDownloading ? t("inventory.qr_codes.downloading") : t("inventory.qr_codes.download")}
                  </Button>

                  <Button variant="ghost" size="icon" onClick={clearSelection} title={t("inventory.clearSelection")}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <InventoryFilters
              filters={filters}
              assetType={assetType}
              assetTypes={assetTypes}
              stages={stages}
              customers={customers}
              locations={locations}
              tags={tags}
              products={products}
              containers={containers}
              volumes={volumes}
              onSearch={handleSearch}
              onAssetTypeChange={(type) => router.get("/inventory", { asset_type: type }, { preserveState: false })}
            />

            {assets.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {hasFilters ? t("inventory.noSearchResults") : t("inventory.noAssets")}
              </p>
            ) : (
              <>
                <InventoryTable
                  assets={assets}
                  assetType={assetType}
                  filters={filters}
                  onSort={handleSort}
                  onEdit={openEditDialog}
                  selectedIds={selectedAssetIds}
                  onSelectionChange={handleSelectionChange}
                  onMoveStage={handleMoveStage}
                />
                <Pagination pagination={pagination} onPageChange={goToPage} />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <MoveStageDialog
        open={isMoveDialogOpen}
        onOpenChange={(open) => handleMoveDialogClose(open, false)}
        onSuccess={() => {
          handleMoveDialogClose(false, true)
        }}
        assets={moveDialogAssets}
        assetType={assetType}
        selectedIds={isBulkMove ? selectedAssetIds : undefined}
        allCodes={isBulkMove ? allSelectedCodes : undefined}
        stages={stages}
        products={products || []}
        customers={customers}
        tags={tags}
      />

      <MoveLocationDialog
        open={isMoveLocationDialogOpen}
        onOpenChange={(open) => handleMoveLocationDialogClose(open, false)}
        onSuccess={() => {
          handleMoveLocationDialogClose(false, true)
        }}
        assetType={assetType}
        selectedIds={selectedAssetIds}
        allCodes={allSelectedCodes}
        locations={locations}
      />
    </AppLayout>
  )
}
